import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'hooks/redux';
import { isEqual } from 'lodash';
import { setElementValues } from 'store/rfiSlice';
import { TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Hint } from '@progress/kendo-react-labels';
import { answerValueTypes, elementTypes, IElementProps } from '../../types/RfiTypes';

const RfiTextBox = (props: IElementProps) => {
  // Hooks
  const dispatch = useAppDispatch();

  // Default value from props
  let defaultValue = props.element.answers
    ? props.element.answers[0]?.value ?? props.defaultValue ?? ""
    : props.defaultValue;

  // max text element length
  const max = 100;

  // State
  const [value, setValue] = useState(defaultValue);

  // Handlers

  useEffect(() => {
    if (props.element.answers && props.element.answers.length > 0) {
      if (props.element.answers[0].value) {
        dispatch(
          setElementValues({
            elementId: props.element.elementId,
            values: [
              {
                value: props.element.answers[0].value.toString(),
                answerValueType: answerValueTypes.DEFAULT,
              },
            ],
          })
        );

        setValue(props.element.answers[0].value.toString());
      }
    }
  }, [props.element.answers]);

  useEffect(() => {
    if (
      props.defaultValue &&
      !(props.element.answers && props.element.answers.length > 0 && props.element.answers[0].value)
    ) {
      dispatch(
        setElementValues({
          elementId: props.element.elementId,
          values: [
            {
              value: props.defaultValue,
              answerValueType: answerValueTypes.DEFAULT,
            },
          ],
        })
      );
    }
  }, [props.defaultValue]);

  const handleChange = (e: TextBoxChangeEvent) => {
    dispatch(
      setElementValues({
        elementId: props.element.elementId,
        values: [
          {
            value: e.target.value?.toString(),
            answerValueType: answerValueTypes.DEFAULT,
          },
        ],
      })
    );

    setValue(e.target.value?.toString() ?? "");
  };

  // Render
  if (props.element.elementType === elementTypes.FIELD) {
    // Step 1
    if (props.readOnly) {
      return (
        <div className="form-group row">
          <div className="col-lg-3 col-form-label">
            {props.element.fieldLabel}
          </div>
          <div className="col-lg-8 fieldElement">{value}</div>
        </div>
      );
    } else
      return (
        <div className="form-group row">
          <div className="col-lg-3 col-form-label">
            <label
              className="col-form-label"
              htmlFor={`textbox-${props.element.elementId}`}
            >
              {props.element.fieldLabel}{" "}
              {props.overrideRequired === undefined ? (
                props.element.requiredResponse ? (
                  <span className="requiredField">(required)</span>
                ) : (
                  ""
                )
              ) : props.overrideRequired === true ? (
                <span className="requiredField">(required)</span>
              ) : (
                ""
              )}
            </label>
          </div>
          <div className="col-lg-8 fieldElement">
            <TextBox
              name={props.element.mapsToField}
              onChange={handleChange}
              value={value}
              maxLength={max}
              id={`textbox-${props.element.elementId}`}
            />
            <Hint direction={"end"}>
              {value?.length ?? 0} / {max}
            </Hint>
          </div>
        </div>
      );
  } else {
    // Steps 3 & 4
    if (props.readOnly) {
      return (
        <>
          <div className="col-form-label">
            {`${props.questionNumber}.`} {props.element.fieldLabel}{" "}
            {props.overrideRequired === undefined ? (
              props.element.requiredResponse ? (
                <span>*</span>
              ) : (
                ""
              )
            ) : props.overrideRequired === true ? (
              <span>*</span>
            ) : (
              ""
            )}
          </div>
          <div>
            {props.showValue ? (
              value
            ) : (
              <div style={{ marginLeft: "20px", marginRight: "10px" }}>
                <input
                  id={`read-only-${props.element.elementId}`}
                  className="form-control"
                  disabled
                  value={value}
                />
                <br />
              </div>
            )}
          </div>
        </>
      );
    } else
      return (
        <>
          <fieldset>
            <legend className="visuallyHidden">
              {props.element.fieldLabel}
            </legend>
            <label
              className="col-form-label"
              htmlFor={`textbox-${props.element.elementId}`}
            >
              <div>
                {`${props.questionNumber}.`} {props.element.fieldLabel}{" "}
                {props.overrideRequired === undefined ? (
                  props.element.requiredResponse ? (
                    <span className="requiredField">(required)</span>
                  ) : (
                    ""
                  )
                ) : props.overrideRequired === true ? (
                  <span className="requiredField">(required)</span>
                ) : (
                  ""
                )}
              </div>
            </label>
            <div>
              <TextBox
                name={props.element.mapsToField}
                onChange={handleChange}
                value={value}
                maxLength={max}
                id={`textbox-${props.element.elementId}`}
              />
              <Hint direction={"end"}>
                {value?.length ?? 0} / {max}
              </Hint>
            </div>
          </fieldset>
        </>
      );
  }
};

// Only re-render if props changed otherwise use memoized version
const areEqual = (prevProps: IElementProps, nextProps: IElementProps) => {
  return (
    !prevProps.rerender &&
    isEqual(prevProps.element.elementId, nextProps.element.elementId) &&
    isEqual(prevProps.element, nextProps.element) &&
    isEqual(prevProps.element.answers, nextProps.element.answers) &&
    isEqual(prevProps.designMode, nextProps.designMode) &&
    isEqual(prevProps.readOnly, nextProps.readOnly)
  );
};

const TextBoxElement = React.memo(RfiTextBox, areEqual);

export default TextBoxElement;
